import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderComponent } from '../../components/header/header.component';
import { SelectInputComponent } from '../../components/select-input/select-input.component';
import { ButtonComponent } from '../../components/button/button.component';
import { GlobalService } from '../../services/global.service';
import { DropdownService } from '../../services/dropdown.service';
import { ExceptionService } from '../../services/exception.service';
import { HttpService } from '../../services/http.service';

@Component({
  selector: 'app-exception-view-page',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent, SelectInputComponent, ButtonComponent],
  templateUrl: './exception-view-page.component.html',
  styleUrl: './exception-view-page.component.css',
})
export class ExceptionViewPageComponent {

  show_resolve_modal = false;

  exception_id: number | null = null;
  script_id: number | null = null;
  exception: any = null;
  script_images: any[] = [];
  current_image_index = 0;

  zoom_level = 1;

  image_max_height: number = window.innerHeight - 80;
  script_page_blur: number[] = [];
  supplement_page_blur: number[] = [];
  supplement_start_index: number = 0;

  selected_action = '';
  comment = '';
  assigned_role_id: number | null = null;
  assigned_user_id: number | null = null;
  evaluator_reply_comment = '';

  is_submitting = false;

  action_options: { title: string; value: string }[] = [];

  role_options: any[] = [];
  user_options: any[] = [];

  constructor(
    public gs: GlobalService,
    public ar: ActivatedRoute,
    public router: Router,
    public es: ExceptionService,
    public dds: DropdownService,
    public http: HttpService,
  ) { }

  async ngOnInit() {
    const perms = this.gs.user_role_data || {};
    const all_actions = [
      { title: 'Reply', value: 'reply', perms: ['exceptions_exceptions_reply', 'exceptions_assigned_exceptions_reply'] },
      { title: 'Assign', value: 'assign', perms: ['exceptions_exceptions_assign', 'exceptions_assigned_exceptions_assign'] },
      { title: 'Reject', value: 'reject', perms: ['exceptions_exceptions_reject', 'exceptions_assigned_exceptions_reject'] },
      { title: 'Mark as Resolved', value: 'mark_as_resolved', perms: ['exceptions_exceptions_mark_as_resolved', 'exceptions_assigned_exceptions_mark_as_resolved'] },
    ];
    this.action_options = all_actions.filter(o => o.perms.some(p => !!perms[p])).map(({ title, value }) => ({ title, value }));

    await this.dds.get_dropdown_list(['role', 'user']);
    const all_roles = this.dds.dropdown_list?.role_list || [];
    this.role_options = all_roles.filter((r: any) => {
      const name = (r.title || r.name || '').toLowerCase();
      return !name.includes('evaluator') && !name.includes('head');
    });
    this.user_options = [];

    this.ar.queryParams.subscribe(async (params) => {
      this.exception_id = Number(params['id']) || null;
      this.script_id = Number(params['script_id']) || null;
      if (this.exception_id || this.script_id) {
        await this.load_exception();
        if (this.assigned_role_id) this.on_role_change();
      }
    });
    this.zoom_in()
  }

  async load_exception() {
    try {
      const res: any = await this.es.get(this.script_id || this.exception_id);
      this.exception = res?.data || res;
      const raw_images = this.exception?.Script?.ScriptImages || [];
      this.script_images = [...raw_images].sort((a: any, b: any) => {
        const nameA = a.image_path?.split('/').pop() || '';
        const nameB = b.image_path?.split('/').pop() || '';
        return nameA.localeCompare(nameB, undefined, { numeric: true });
      });
      this.calculate_page_blur();
      // Pre-fill action if already taken
      if (this.exception?.status && this.exception.status !== 'New') {
        const is_self_assigned = this.exception.status === 'Assigned' && this.exception.assigned_user_id === this.current_user_id;
        if (is_self_assigned) {
          // Assigned to the current user themselves: treat as a reply, show comment only
          this.selected_action = 'reply';
          this.comment = this.exception.comment || '';
        } else {
          const resolved_action = this.get_action_from_status(this.exception.status);
          this.comment = this.exception.comment || '';
          // Only pre-fill if the action is actually selectable for this viewer,
          // otherwise leave everything blank instead of showing a half-filled form
          if (this.action_options.some(o => o.value === resolved_action)) {
            this.selected_action = resolved_action;
            this.comment = this.exception.comment || '';
            this.assigned_role_id = this.exception.assigned_role_id || null;
            this.assigned_user_id = this.exception.assigned_user_id || null;
          }
        }
      }
    } catch (e: any) {
      this.gs.toastr_shows_function(e?.error?.message || 'Failed to load exception', 'Error', 'error');
    }
  }

  get_action_from_status(status: string): string {
    const map: any = { Replied: 'reply', Assigned: 'assign', Rejected: 'reject', Resolved: 'mark_as_resolved' };
    return map[status] || '';
  }

  calculate_page_blur() {
    const script = this.exception?.Script;
    const noOfPagesPerScript = parseInt(script?.no_of_pages_per_script || '0');
    const noOfPagesPerSupplement = parseInt(script?.no_of_pages_per_supplement || '0');
    const hideScriptPagesForFaculty = parseInt(script?.hide_script_pages_for_faculty || '0');
    const hideScriptPercentage = parseInt(script?.hide_script_percentage || '0');
    const hideSupplementPagesForFaculty = parseInt(script?.hide_supplement_pages_for_faculty || '0');
    const hideSupplementPercentage = parseInt(script?.hide_supplement_percentage || '0');

    this.supplement_start_index = noOfPagesPerScript;
    this.script_page_blur = this.calculatePerPageBlur(hideScriptPagesForFaculty, hideScriptPercentage);

    const blurPerGroup = this.calculatePerPageBlur(hideSupplementPagesForFaculty, hideSupplementPercentage);
    const totalSupplementPages = Math.max(0, this.script_images.length - noOfPagesPerScript);
    this.supplement_page_blur = [];
    if (noOfPagesPerSupplement > 0 && hideSupplementPagesForFaculty > 0 && hideSupplementPercentage > 0) {
      for (let i = 0; i < totalSupplementPages; i++) {
        const posInGroup = noOfPagesPerSupplement > 0 ? i % noOfPagesPerSupplement : i;
        this.supplement_page_blur.push(blurPerGroup[posInGroup] ?? 0);
      }
    }
  }

  // Calculate per-page blur percentages
  // E.g. pages=2, percentage=75 → total coverage = 2*0.75 = 1.5 pages
  // → Page 0: 100%, Page 1: 50%
  calculatePerPageBlur(pages: number, percentage: number): number[] {
    if (pages <= 0 || percentage <= 0) return [];
    const totalCoverage = pages * (percentage / 100);
    const result: number[] = [];
    let remaining = totalCoverage;
    for (let i = 0; i < pages; i++) {
      if (remaining >= 1) { result.push(100); remaining -= 1; }
      else if (remaining > 0) { result.push(Math.round(remaining * 100)); remaining = 0; }
      else { result.push(0); }
    }
    return result;
  }

  get current_image() {
    const path = this.script_images[this.current_image_index]?.image_path;
    if (!path) return null;
    return `${this.http.base_url}/media/get-file?file_path=scripts/${path}`;
  }

  get current_image_blur_percent(): number {
    const i = this.current_image_index;
    if (i < this.supplement_start_index) return this.script_page_blur[i] || 0;
    return this.supplement_page_blur[i - this.supplement_start_index] || 0;
  }

  prev_image() { if (this.current_image_index > 0) this.current_image_index--; }
  next_image() { if (this.current_image_index < this.script_images.length - 1) this.current_image_index++; }
  zoom_in() { this.zoom_level = Math.min(this.zoom_level + 0.25, 3); }
  zoom_out() { this.zoom_level = Math.max(this.zoom_level - 0.25, 1.25); }
  reset_zoom() { this.zoom_level = 1; this.zoom_in(); }

  on_action_change() {
    this.comment = '';
    this.assigned_role_id = null;
    this.assigned_user_id = null;
  }

  on_role_change() {
    this.assigned_user_id = null;
    if (!this.assigned_role_id) {
      this.user_options = [];
      return;
    }
    const assessment = this.exception?.Script?.Assessment;
    const university_id = assessment?.university_id;
    const subject_id = assessment?.subject_id;
    const all_users = this.dds.dropdown_list?.user_list || [];
    this.user_options = all_users.filter((u: any) => {
      const role_match = u.role_id === this.assigned_role_id;
      const university_match = !university_id || !u.university_id || u.university_id == university_id;
      const has_subjects = Array.isArray(u.subject_ids) && u.subject_ids.length > 0;
      const subject_match = !has_subjects || !subject_id || u.subject_ids.includes(+subject_id);
      return role_match && university_match && subject_match;
    });
  }

  async submit() {
    if (!this.selected_action) {
      this.gs.toastr_shows_function('Please select an action', 'Error', 'error');
      return;
    }
    if (this.selected_action === 'assign') {
      if (!this.assigned_role_id) {
        this.gs.toastr_shows_function('Please select a role', 'Error', 'error');
        return;
      }
      if (!this.assigned_user_id) {
        this.gs.toastr_shows_function('Please select a user', 'Error', 'error');
        return;
      }
    }
    if (this.selected_action === 'mark_as_resolved') {
      this.show_resolve_modal = true;
      return;
    }
    await this.do_submit();
  }

  async confirm_resolve() {
    this.show_resolve_modal = false;
    await this.do_submit();
  }

  async do_submit() {
    try {
      this.is_submitting = true;
      const payload: any = { id: this.exception_id, action: this.selected_action, comment: this.comment };
      if (this.assigned_role_id) payload.assigned_role_id = this.assigned_role_id;
      if (this.assigned_user_id) payload.assigned_user_id = this.assigned_user_id;

      const res: any = await this.es.update_exception(payload);
      if (res?.code === 200) {
        this.gs.toastr_shows_function(res.message || 'Exception updated successfully', 'Success', 'success');
        this.gs.go_back();
      } else {
        this.gs.toastr_shows_function(res?.message || 'Failed to update', 'Error', 'error');
      }
    } catch (e: any) {
      this.gs.toastr_shows_function(e?.error?.message || 'Something went wrong', 'Error', 'error');
    } finally {
      this.is_submitting = false;
    }
  }

  get evaluator_name() { return this.exception?.User?.name || '-'; }
  get evaluator_role() { return this.exception?.User?.Role?.name || 'Evaluator'; }
  get is_view_only(): boolean {
    const role_name: string = (this.gs.user_role_data?.name || '').toLowerCase();
    return role_name.includes('evaluator');
  }
  get evaluator_contact() { return this.exception?.User?.contact || '-'; }
  get evaluator_email() { return this.exception?.User?.email || '-'; }
  get exception_types() {
    const e = this.exception?.exception;
    return Array.isArray(e) ? e.join(', ') : (e || '-');
  }
  get description() { return this.exception?.description || '-'; }

  get current_user_id(): number { return this.gs.user_data?.id || 0; }

  get is_admin(): boolean {
    return !!this.gs.user_role_data?.exceptions_permission;
  }

  get evaluator_can_reply(): boolean {
    if (!this.exception || this.is_admin) return false;
    const status = this.exception.status;
    if (this.is_view_only && status === 'Replied') return true;
    if (status === 'Assigned' && this.exception.assigned_user_id === this.current_user_id) return true;
    return false;
  }

  async submit_evaluator_reply() {
    if (!this.evaluator_reply_comment.trim()) {
      this.gs.toastr_shows_function('Comment is required', 'Error', 'error');
      return;
    }
    try {
      this.is_submitting = true;
      const res: any = await this.es.update_exception({ id: this.exception_id, action: 'evaluator_reply', comment: this.evaluator_reply_comment });
      if (res?.code === 200) {
        this.gs.toastr_shows_function('Reply sent successfully', 'Success', 'success');
        this.gs.go_back();
      } else {
        this.gs.toastr_shows_function(res?.message || 'Failed to send reply', 'Error', 'error');
      }
    } catch (e: any) {
      this.gs.toastr_shows_function(e?.error?.message || 'Something went wrong', 'Error', 'error');
    } finally {
      this.is_submitting = false;
    }
  }
}
